import styled, { css, keyframes, createGlobalStyle } from 'styled-components'
import {
  borderRadius,
  rem,
  BaseColor,
  spacing,
  themeColor,
  transitionDuration,
  transitions,
  Typography,
  opaqueColor,
  breakpointEnding,
  Breakpoint,
} from '~theme'

export type BackgroundBlurValue = 'small' | 'medium' | 'large'

interface OverlayProps {
  isVisible: boolean
  backgroundBlur?: BackgroundBlurValue
  zIndex: number
}

const modalAnimation = keyframes`
  from {
    opacity: 0;
  }
`
const animate = css`
  animation: ${modalAnimation} ${transitionDuration}ms forwards ease-out;
`

export const BodyOverflowStyle = createGlobalStyle`
  body {
    overflow: hidden;
  }
`

export const Overlay = styled.div<OverlayProps>`
  display: ${props => (props.isVisible ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: fixed;
  z-index: ${props => props.zIndex};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: ${spacing()};
  background-color: ${props => opaqueColor(themeColor(BaseColor.overlay)(props), 70)};
  ${props => {
    switch (props.backgroundBlur) {
      case 'small':
        return css`
          backdrop-filter: blur(1px);
        `
      case 'medium':
        return css`
          backdrop-filter: blur(3px);
        `
      case 'large':
        return css`
          backdrop-filter: blur(8px);
        `
      default:
        return null
    }
  }}
  ${animate};
`

export const Window = styled.section<{ withSurface: boolean }>`
  position: relative;
  flex-shrink: 1;
  overflow: auto;
  min-height: ${spacing(5)};
  min-width: ${spacing(5)};
  padding: ${spacing(1.5)};

  ${props =>
    props.withSurface &&
    css`
      background-color: ${themeColor(BaseColor.surfaceBg)};
      border: 1px solid ${themeColor(BaseColor.shade_30)};
      border-radius: ${borderRadius};
    `};

  @media (${breakpointEnding(Breakpoint.md)}) {
    padding: ${spacing(1)};
  }
`

export const CloseButton = styled.button<{ fixedToScreen: boolean }>`
  position: ${props => (props.fixedToScreen ? 'fixed' : 'absolute')};
  top: 0;
  right: 0;
  padding: ${spacing(1.5)} ${spacing(1.5)} ${spacing(0.5)} ${spacing(1)};
  color: ${themeColor(BaseColor.shade_50)};
  ${transitions(['color'])}

  &:hover {
    color: ${themeColor(BaseColor.shade_70)};
  }

  @media (${breakpointEnding(Breakpoint.md)}) {
    padding-top: ${spacing(1)};
    padding-right: ${spacing(1)};
  }
`

const closeButtonSpacing = spacing(2)

export const Header = styled.header`
  margin-right: ${closeButtonSpacing};
`

export const Title = styled.h5`
  ${Typography.titleLarge};
  ${Typography.textRegular};
  margin-top: ${rem(-2)};
  margin-right: ${rem(32)};
  color: ${themeColor(BaseColor.shade_70)};
`

export interface BodyProps {
  spaceForClose?: boolean
}

export const Body = styled.div<BodyProps>`
  margin-right: ${props => (props.spaceForClose ? closeButtonSpacing : '0')};
  color: ${themeColor(BaseColor.shade_60)};
  ${Header} + & {
    margin-top: ${rem(12)};
  }
`

export const MediumBody = styled(Body)`
  max-width: ${spacing(31)};
`

export const Footer = styled.footer`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing(0.5)};
  margin-top: ${rem(18)};
`
